// KB API contract types — wire shapes for the admin Knowledge Base endpoints.
//
// **The single most important type in this file is `KBEntry`** — a
// discriminated union over the 4 content types. Every other type either
// derives from it or references it.
//
// Design references:
//   - design/data-model-v0.md § 3.4 (KBEntry), § 3.8 (SourceDocument), § 3.9 (versioning)
//   - design/UI : UX v1.0/for-engineering-knowledge/engineering-brief.md
//   - memory/project_kb_architecture.md (to be created)
//
// Lifecycle states mirror Users + Tenants (no hard delete; soft remove +
// super_admin-only restore via type-to-confirm).

import type { Domain } from './domains.js';
// SourceDocumentId is canonically declared in source-documents-api.ts (the
// owning module). Imported here for the document_excerpt body's
// `source_document_id` field — both modules stay consistent without a
// duplicate type declaration.
import type { SourceDocumentId } from './source-documents-api.js';

/** Stable, opaque KB entry id — Mongo ObjectId serialized as a 24-char hex string. */
export type KBEntryId = string;

/**
 * The 4 polymorphic content types. Each has a different body shape.
 * Authored types: qa_pair, framework, case_study.
 * Generated type: document_excerpt (produced by ingestion pipeline; editorial
 * team reviews + tags but doesn't author the body text).
 */
export type KBEntryType = 'qa_pair' | 'framework' | 'case_study' | 'document_excerpt';

export const ALL_KB_ENTRY_TYPES: ReadonlyArray<KBEntryType> = [
  'qa_pair',
  'framework',
  'case_study',
  'document_excerpt',
] as const;

/**
 * Authored types — the ones editors can create from scratch in the editor.
 * `document_excerpt` is NOT in this list because it's pipeline-generated and
 * editors reach it via the review queue, not the create flow.
 */
export const AUTHORED_KB_ENTRY_TYPES: ReadonlyArray<KBEntryType> = [
  'qa_pair',
  'framework',
  'case_study',
] as const;

/**
 * Lifecycle status. Mirrors Users + Tenants with KB-specific additions
 * (`draft`, `in_review`, `published`, `archived`) for the editorial workflow.
 *
 *   - `draft`     — author work-in-progress
 *   - `in_review` — submitted for editorial review
 *   - `published` — live; eligible for retrieval in coaching responses
 *   - `archived`  — withdrawn from retrieval but kept for audit; reversible
 *                   to `published` without restore friction
 *   - `removed`   — permanent offboarding signal (per the no-hard-delete
 *                   architecture); hidden from default views; restorable only
 *                   by super_admin via type-to-confirm "RESTORE"
 *
 * Hard delete is never used (SCP would not block KB-collection writes — that
 * SCP is Cognito-only — but the application enforces it at the handler layer).
 */
export type KBEntryStatus =
  | 'draft'
  | 'in_review'
  | 'published'
  | 'archived'
  | 'removed';

export type KBEntryLanguage = 'en' | 'fr';

/**
 * Segment tags — structured metadata for retrieval pre-filtering. Each entry
 * can be tagged with sector / revenue band / dept size / geography hints so
 * the retrieval pipeline can prefer entries that match the asking user's
 * GCProfile. Uses the same enums as `GCProfile` and `case_study.anonymized_context`
 * — engineering note: those enums are not yet locked (pending Olivier on §6 Q1–Q3
 * of the working session); for V1 the segment-tag fields accept any string,
 * narrowed once the enums are confirmed.
 */
export interface KBEntrySegmentTags {
  sectors?: string[];
  revenue_bands?: string[];
  dept_sizes?: string[];
  geographies?: string[];
}

// ─── Per-type body shapes ──────────────────────────────────────────────────

/**
 * `qa_pair` body — a direct question + variant phrasings + a curated answer.
 * The largest-volume content type (300+/domain expected per Olivier).
 */
export interface QAPairBody {
  question: string;
  /** Alternate phrasings of the same question, used by retrieval to match user wording. */
  question_variants: string[];
  /** Rich-text answer. Markdown or block-encoded; rendering is the SPA's concern. */
  answer: string;
}

/**
 * `framework` body — a methodology with sequential phases (each with its own
 * activities and deliverables). Pending Olivier confirmation on whether some
 * frameworks are parallel "dimensions" instead of sequential phases — V1
 * supports phases only; dimensions would be an additive change.
 */
export interface FrameworkBody {
  methodology_name: string;
  /** Trigger condition / context for when to apply this methodology. */
  when_to_apply: string;
  phases: ReadonlyArray<FrameworkPhase>;
}

export interface FrameworkPhase {
  name: string;
  description: string;
  key_activities: string[];
  deliverables: string[];
}

/**
 * `case_study` body — anonymized client narrative. Three rich-text sections
 * (situation / decision / outcome) plus a lessons list. Pending Olivier
 * confirmation on whether to keep 3 sections or collapse to one narrative
 * field — V1 ships 3 sections (collapsing later is trivial; expanding is harder).
 */
export interface CaseStudyBody {
  /** One-line classification — e.g. "Mid-size tech GC consolidating panel from 40 → 12 firms". */
  archetype: string;
  /** Structured context — reuses the same enums as GCProfile. */
  anonymized_context: CaseStudyContext;
  situation: string;
  decision: string;
  outcome: string;
  lessons: string[];
}

export interface CaseStudyContext {
  sector?: string;
  revenue_band?: string;
  dept_size?: string;
  /** Either a single region/country or an array (multi-jurisdiction clients). */
  geography?: string | string[];
}

/**
 * `document_excerpt` body — generated by the SourceDocument ingestion pipeline.
 * NOT authored. Editorial team reviews the auto-assigned domain + the excerpt
 * text and either approves, re-tags, rejects, OR EDITS to refresh outdated
 * passages (Olivier's 2014 book is a real example — he wants the ability
 * to update wording without losing provenance).
 *
 * Two text fields:
 *   - `original_text_chunk`: verbatim from the book, set once by ingestion,
 *     NEVER mutated. Powers honest citations.
 *   - `text_chunk`: the editorial-current version. Equal to original on
 *     insert; diverges if editorial reworks the passage. This is what the
 *     embedding indexes + the coaching engine retrieves.
 *
 * Citation rule (Phase 4 RAG composer):
 *   - text_chunk === original_text_chunk → "Chaduteau (2014), Ch.1.B"
 *   - text_chunk !== original_text_chunk → "Adapté de Chaduteau (2014), Ch.1.B"
 */
export interface DocumentExcerptBody {
  /** FK to the parent SourceDocument (the book / long doc this excerpt came from). */
  source_document_id: SourceDocumentId;
  /** Chapter identifier — used in citations. E.g. "Chapter 5" or "5". */
  chapter: string;
  /** Section identifier within the chapter — used in citations. E.g. "5.2" or "Panel Rationalization". */
  section: string;
  /** Optional page range — depends on source format (PDF has it, EPUB usually doesn't). */
  page_range?: string;
  /**
   * Editorial-current excerpt text. Editable from the review queue (and
   * any subsequent edit pass). This is what the embedding indexes + the
   * RAG layer retrieves. On insert by the ingestion Lambda, equals
   * `original_text_chunk`; diverges when editorial reworks.
   */
  text_chunk: string;
  /**
   * Verbatim excerpt as it appeared in the source book. Set once by the
   * ingestion Lambda; NEVER mutated by editorial. Citation footers compare
   * this to `text_chunk` to decide whether to label the citation
   * "Chaduteau (2014)" (verbatim) vs "Adapté de Chaduteau (2014)" (edited).
   */
  original_text_chunk: string;
  /** ISO timestamp set by the PATCH handler when text_chunk diverges from original_text_chunk. */
  edited_at?: string;
  /** Cognito sub of the editor who last reworked this excerpt. Set alongside edited_at. */
  edited_by?: string;
  /** Auto-classifier's suggested domain (before editorial review). */
  suggested_domain?: Domain;
  /** Classifier confidence 0.0–1.0 — editorial reviews lowest-confidence first. */
  confidence?: number;
  /** Whether editorial has approved this excerpt for retrieval. Set by review action. */
  editorial_status?: 'pending_review' | 'approved' | 'rejected';
  /**
   * Estimated token count of `text_chunk` at ingestion time — populated
   * by the chunker. Optional on legacy records. Used by the review queue
   * to surface chunks whose size may hurt retrieval quality (see
   * `ChunkSizeTier` in source-document-mapping-api.ts).
   */
  estimated_tokens?: number;
  /**
   * True when this excerpt was produced by the chunker's auto-split
   * fallback (a sub-section exceeded MAX_TOKENS_PER_CHUNK and got
   * paragraph-split with overlap). Signals to the reviewer that the
   * upstream section was too broad and the map could be tightened.
   */
  was_auto_split?: boolean;
  /**
   * When `was_auto_split=true`: which window this is (1-indexed) and
   * how many total windows the parent section was split into.
   */
  split_index?: number;
  split_total?: number;
  /**
   * Contextual retrieval blurb — a 2-3 sentence context statement
   * generated by Haiku at ingest time, situating this excerpt within
   * its larger document. PREPENDED to the chunk text when building the
   * embedding input (see embeddings.ts::buildEmbeddingText). NOT shown
   * to editors in the review UI — the raw chunk is what's displayed +
   * cited. Optional on legacy records ingested before contextual
   * retrieval was enabled.
   *
   * Anthropic contextual-retrieval reference:
   * https://www.anthropic.com/news/contextual-retrieval
   */
  context_blurb?: string;
}

// ─── KBEntry — the discriminated union ─────────────────────────────────────

/**
 * Common envelope — fields every KB entry carries regardless of type.
 * The discriminating fields (`type`, `body`) live on the union members below.
 */
interface KBEntryBase {
  id: KBEntryId;
  /** The substantive area this entry covers. */
  domain: Domain;
  status: KBEntryStatus;
  language: KBEntryLanguage;
  /** Incrementing integer; bumps on every publish from draft/in_review/archived. */
  version: number;
  /** Display name of the person who originally authored the entry. */
  contributor: string;
  /** Display name of the person who last edited (may equal contributor on a single-author entry). */
  last_edited_by: string;
  /** Retrieval pre-filter tags — reuse GCProfile enums where applicable. */
  segment_tags: KBEntrySegmentTags;
  /** Optional one-line title for table display. For qa_pair this is often the question; for framework it's the methodology_name; for case_study it's the archetype; for document_excerpt it's the section name. Derived/denormalized for fast table queries. */
  title: string;
  /** ISO-8601 timestamps. */
  createdAt: string;
  updatedAt: string;
  /** Set when status first hits 'published'. Null otherwise. Stays set even after archive (so we know it WAS published once). */
  publishedAt: string | null;
  /** Set when status transitions to 'archived'. Null otherwise. */
  archivedAt: string | null;
  /** Set when status transitions to 'removed'. Null otherwise. */
  removedAt: string | null;
  /** Display name of the actor who removed this entry. Null unless status='removed'. */
  removedBy: string | null;
  /** Optional free-text reason captured at removal time. Null unless supplied. */
  removedReason: string | null;
}

/** A single KB entry. Type-discriminated on `type`; body shape depends on the discriminator. */
export type KBEntry =
  | (KBEntryBase & { type: 'qa_pair'; body: QAPairBody })
  | (KBEntryBase & { type: 'framework'; body: FrameworkBody })
  | (KBEntryBase & { type: 'case_study'; body: CaseStudyBody })
  | (KBEntryBase & { type: 'document_excerpt'; body: DocumentExcerptBody });

/**
 * The minimal shape rendered in the KB list table. Strips the body to keep
 * list payloads small (a published `framework` body can be many KB).
 */
export interface KBEntrySummary {
  id: KBEntryId;
  type: KBEntryType;
  title: string;
  domain: Domain;
  status: KBEntryStatus;
  language: KBEntryLanguage;
  version: number;
  contributor: string;
  segment_tags: KBEntrySegmentTags;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

// ─── GET /kb-entries ───────────────────────────────────────────────────────

export type KBEntryStatusFilter = KBEntryStatus | 'all';

export interface ListKBEntriesQuery {
  /** Default: hides removed. 'all' includes EVERY status. */
  status?: KBEntryStatusFilter;
  type?: KBEntryType;
  domain?: Domain;
  language?: KBEntryLanguage;
  /** Case-insensitive search across title + body text (best-effort regex). */
  q?: string;
}

export interface ListKBEntriesResponse {
  entries: ReadonlyArray<KBEntrySummary>;
  counts: {
    total: number;
    draft: number;
    in_review: number;
    published: number;
    archived: number;
    removed: number;
  };
}

// ─── GET /kb-entries/:id ───────────────────────────────────────────────────

export interface GetKBEntryResponse {
  entry: KBEntry;
}

// ─── POST /kb-entries ──────────────────────────────────────────────────────

/**
 * Create a KB entry. Status is always 'draft' on create — moving to
 * 'in_review' / 'published' happens via lifecycle endpoints below.
 * `document_excerpt` is NOT creatable via this endpoint (returns 400) —
 * those come from the ingestion pipeline. Validation enforces the
 * type/body shape match.
 */
export type CreateKBEntryRequest =
  | { type: 'qa_pair'; domain: Domain; language: KBEntryLanguage; segment_tags?: KBEntrySegmentTags; body: QAPairBody }
  | { type: 'framework'; domain: Domain; language: KBEntryLanguage; segment_tags?: KBEntrySegmentTags; body: FrameworkBody }
  | { type: 'case_study'; domain: Domain; language: KBEntryLanguage; segment_tags?: KBEntrySegmentTags; body: CaseStudyBody };

export interface CreateKBEntryResponse {
  entry: KBEntry;
}

// ─── POST /kb-entries/:id/duplicate ────────────────────────────────────────

/**
 * Duplicate a KB entry. V1 scope (per Olivier 2026-10-22): case_study
 * only — he writes multiple case studies on the same theme and wants
 * to clone one as a starting point then edit the varying fields
 * (industry, team size, context). Other KB types return 400 until the
 * scope widens.
 *
 * The clone is a fresh entry: new id, version=1, status='draft', the
 * current user as contributor, `(copy) ` prefix on body.archetype so
 * the duplicate is immediately recognisable in the list. All lifecycle
 * timestamps reset.
 *
 * Request body is empty — the source id is on the path.
 */
export type DuplicateKBEntryRequest = Record<string, never>;
export interface DuplicateKBEntryResponse {
  entry: KBEntry;
}

// ─── PATCH /kb-entries/:id ─────────────────────────────────────────────────

/**
 * Partial update. Cannot change `type` (would invalidate the body shape) or
 * lifecycle status (use the dedicated endpoints). Body is patchable IF the
 * supplied shape matches the entry's existing type.
 *
 * Updating a `published` entry creates a draft revision in the editor's
 * working state but does NOT bump the version until re-publish — the
 * version-bump happens on the publish endpoint.
 */
export interface PatchKBEntryRequest {
  domain?: Domain;
  language?: KBEntryLanguage;
  segment_tags?: KBEntrySegmentTags;
  /** Body shape MUST match the existing entry's type. Type-mismatched bodies are rejected. */
  body?: QAPairBody | FrameworkBody | CaseStudyBody | DocumentExcerptBody;
}

export interface PatchKBEntryResponse {
  entry: KBEntry;
}

// ─── Lifecycle transitions ─────────────────────────────────────────────────

/** POST /kb-entries/:id/submit-review — draft → in_review. */
/** POST /kb-entries/:id/publish — in_review or draft → published. Increments version. */
/** POST /kb-entries/:id/archive — published → archived. */
/** POST /kb-entries/:id/unarchive — archived → published. */

export interface KBEntryLifecycleResponse {
  entry: KBEntry;
}

// ─── DELETE /kb-entries/:id ────────────────────────────────────────────────

/** Permanent offboarding (soft remove). Hidden from default views; super_admin can restore. */
export interface DeleteKBEntryRequest {
  reason?: string;
}

export interface DeleteKBEntryResponse {
  entry: KBEntry;
}

// ─── POST /kb-entries/:id/restore ──────────────────────────────────────────

/** Restore a removed entry. Super_admin only (role-gated at the handler). Returns to status='draft' so the actor can re-review before re-publishing. */
export interface RestoreKBEntryResponse {
  entry: KBEntry;
}

// ─── Document-excerpt-specific review action ───────────────────────────────

/**
 * Editorial review action on a document_excerpt entry. NOT applicable to
 * the other 3 types. Approve = mark editorial_status='approved' AND set
 * status to 'published' (excerpts go through a simpler workflow than authored
 * entries — they don't need draft/review/publish because the body is already
 * canonical). Re-tag = update domain + suggested_domain. Reject = mark
 * editorial_status='rejected' + set status='archived' so it's excluded from
 * retrieval but kept for audit (review may flip back later).
 */
export interface ReviewExcerptRequest {
  action: 'approve' | 'retag' | 'reject';
  /** Required when action='retag'. */
  domain?: Domain;
  /** Optional rejection reason for audit. */
  reason?: string;
}

export interface ReviewExcerptResponse {
  entry: KBEntry;
}

// ─── POST /kb-entries/search — semantic retrieval ──────────────────────────

/**
 * Semantic search across KB entries. Embeds the query, runs Atlas
 * `$vectorSearch` against the `embedding` field, returns top-K results
 * with their cosine-similarity scores.
 *
 * Default scope:
 *   - Only `published` entries are searched (unless `include_unpublished` is
 *     set — useful for editorial preview before publishing).
 *   - All 4 types included unless filtered.
 *   - All 8 domains + both languages included unless filtered.
 *
 * V1 returns entry summaries (not full bodies) to keep payloads small. The
 * caller can fetch full bodies via GET /kb-entries/{id} for the entries
 * actually needed.
 */
export interface SearchKBEntriesRequest {
  query: string;
  /** Number of results to return. Default 10. Max 50 (Atlas vector search soft cap). */
  top_k?: number;
  /** Filter to a specific type. Default: all 4 types. */
  type?: KBEntryType;
  /** Filter to a specific domain. Default: all 8 domains. */
  domain?: Domain;
  /** Filter to a specific language. Default: both EN + FR. */
  language?: KBEntryLanguage;
  /** Include draft/in_review/archived entries (editorial preview). Default: false. */
  include_unpublished?: boolean;
}

export interface SearchKBEntryResult {
  entry: KBEntrySummary;
  /** Cosine-similarity score, 0-1. Higher = closer match. */
  score: number;
}

export interface SearchKBEntriesResponse {
  query: string;
  results: ReadonlyArray<SearchKBEntryResult>;
}

/**
 * ============================================================================
 * POST /kb-entries/{id}/suggest-variants — AI-assisted question-variant
 * generation. Content-editor gated. qa_pair-only. Ephemeral (nothing
 * persisted server-side; the editor curates client-side and appends
 * accepted variants to `body.question_variants` on save).
 *
 * Design references:
 *   - design/UI : UX v1.0/for-engineering-kb/README.md § 6b
 *   - design/UI : UX v1.0/for-engineering-kb/specs/kb-ai-variants-strings.md
 * ============================================================================
 */

export interface SuggestQaPairVariantsRequest {
  /**
   * Variants the client already has (persisted `body.question_variants` plus
   * any in-session accepted candidates from a prior `Suggest more` click) —
   * the LLM is instructed to avoid repeating any of them.
   * Optional; server defaults to persisted `question_variants` alone.
   */
  exclude_variants?: string[];
}

export interface SuggestQaPairVariantsResponse {
  variants: string[];
}
